import { ApiError } from 'src/api/generated';

/**
 * Extracts a user-friendly error message from API errors
 *
 * This utility handles various types of errors that can occur during API calls:
 * - ApiError objects from the generated client with structured error bodies
 * - Standard Error objects
 * - String error messages
 * - Object errors with message properties
 *
 * Priority order for extracting messages:
 * 1. error.body.error (specific backend error message)
 * 2. error.statusText (HTTP status text, if not generic)
 * 3. error.message (error message, if not generic)
 * 4. Status code specific messages
 * 5. Fallback default message
 *
 * @param error - The error object from API calls (can be ApiError, Error, string, or object)
 * @returns A user-friendly error message string in Spanish
 *
 * @example
 * ```typescript
 * try {
 *   await apiCall();
 * } catch (error) {
 *   const message = extractApiErrorMessage(error);
 *   showAlert(message, 'error');
 * }
 * ```
 */
export const extractApiErrorMessage = (error: unknown): string => {
  // TODO: check when implementing i18n
  const defaultMessage = 'Ha ocurrido un error inesperado';

  if (error instanceof ApiError) {
    if (error.body && typeof error.body === 'object') {
      const bodyMessage = (error.body as { message?: string }).message;
      if (typeof bodyMessage === 'string' && bodyMessage.trim()) {
        return bodyMessage;
      }

      const bodyError = (error.body as { error?: string }).error;
      if (typeof bodyError === 'string' && bodyError.trim()) {
        return bodyError;
      }
    }

    if (error.statusText && error.statusText !== 'Bad Request') {
      return error.statusText;
    }

    if (error.message && error.message !== 'Bad Request') {
      return error.message;
    }

    switch (error.status) {
      case 400:
        return 'Los datos enviados no son válidos';
      case 401:
        return 'No tienes autorización para realizar esta acción';
      case 403:
        return 'No tienes permisos para realizar esta acción';
      case 404:
        return 'El recurso solicitado no fue encontrado';
      case 409:
        return 'El recurso ya existe o hay un conflicto';
      case 422:
        return 'Los datos proporcionados no son válidos';
      case 500:
        return 'Ocurrió un error. Por favor, inténtalo de nuevo';
      default:
        return defaultMessage;
    }
  }

  if (error instanceof Error) {
    return error.message || defaultMessage;
  }

  if (typeof error === 'string') {
    return error.trim() || defaultMessage;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: string }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return defaultMessage;
};
